import type { MouseEvent } from 'react';

import type { EditorSize } from '../types';

/**
 * Froala's toolbar button: 18px glyph in a square hit area, tinted on hover and while active,
 * with the glyph in the primary colour when the format is on (`.fr-command.fr-active`).
 * - Smaller inside a size-S toolbar (`data-size="s"` on the `group/toolbar`); popups stay full size, as in Froala.
 */
export const toolbarButtonClasses = (active: boolean, disabled: boolean) =>
  [
    'flex h-8 items-center justify-center gap-px rounded-md px-1.5 transition-colors group-data-[size=s]/toolbar:h-7 group-data-[size=s]/toolbar:px-1',
    disabled ? 'cursor-not-allowed opacity-40' : 'cursor-pointer hover:bg-background-mid-1',
    active ? 'bg-background-mid-1 text-primary-100' : 'text-black',
  ].join(' ');

/** A toolbar button's glyph; see `toolbarButtonClasses`. */
export const toolbarIconClasses = 'size-[18px] group-data-[size=s]/toolbar:size-4';

/** The text and its padding, per size (S matches Froala's `[data-size='s'] .fr-view`). */
export const contentClasses: Record<EditorSize, string> = { m: 'text-pbk-16 p-2', s: 'text-pbk-14 p-1' };

/** The placeholder, overlaid at the content's padding. */
export const placeholderClasses: Record<EditorSize, string> = { m: 'top-2 left-2', s: 'top-1 left-1' };

/** The content's vertical padding, for sizing it by `rows`. */
export const contentPaddingY: Record<EditorSize, string> = { m: '1rem', s: '0.5rem' };

/** One line of content tall, so controls beside the text centre on a single line and sit at the bottom on more. */
export const lineBoxClasses: Record<EditorSize, string> = { m: 'h-10', s: 'h-7' };

/** The white floating panel every editor popup sits in. */
export const panelClasses = 'z-popover border-greyscale-15 rounded-lg border bg-white shadow-lg';

/** Text inputs inside popup forms (link URL, image URL, alt text). */
export const fieldClasses = 'border-greyscale-15 text-pbk-14 w-full rounded-md border px-2 py-1 outline-none focus:border-accent-1-100';

/** The confirm button inside popup forms. */
export const primaryButtonClasses =
  'bg-accent-1-100 text-pbk-14 active:bg-accent-1-75 cursor-pointer rounded-lg px-3 py-1.5 font-medium text-white disabled:opacity-50';

/** Editor controls must never take focus from the content, or the selection is lost. */
export const keepSelection = (e: MouseEvent) => e.preventDefault();
